import type { Saveable } from "../common/persistence";
import type { KeyEventType } from "../interop/types";
import type { WebRichTextObjectEditorInfo } from "../store/textEditor";
import type { AxAttributedString } from "./attributedString";
import Document from "@tiptap/extension-document";
import { anyPressed, BROWSER_SHORTCUTS, isModShortcut } from "ls/common/keyboardShortcuts";
import { read, write } from "../common/persistence";
import { gotFocus, keyEvent, lostFocus, previewGotFocus, previewLostFocus, selectionChanged } from "../interop/dotnetExports/textEditor";
import { attributedStringToJsonContent, defaultParagraphStyle, defaultRunStyle, jsonContentToAttributedString, jsonContentToText } from "../rich-text/converter";
import { normalize, serialize } from "../rich-text/useRichText";
import { useDiagramTextEditorsStore } from "../store/textEditor";
import { AxStringAlignment, getAxStyleBaseStyle, getEmptyAttributedString } from "./attributedString";
import { getEventModifierKeys } from "./utils";

export function doSelectionChanged(id: number) {
  selectionChanged(id);
  return true;
}

export function doGotFocus(id: number, e: FocusEvent) {
  let val = previewGotFocus(id);
  if (val) {
    e.preventDefault();
    return;
  }
  val = gotFocus(id);
  if (val) e.preventDefault();
}

export function doLostFocus(id: number, e: FocusEvent) {
  let val = previewLostFocus(id);
  if (val) {
    e?.preventDefault();
    return;
  }
  val = lostFocus(id);
  if (val) e?.preventDefault();
}

export function sendKeyEvent(id: number, event: KeyboardEvent, eventType: KeyEventType) {
  // I've decided to try this approach which is to basically trap all shortcuts in the rp codebase during
  // text editing except the ones that we want to allow the browser to handle. We will probably need to augment
  // this list over time, or we may need to revisit this approach. The problem is that tiptap plugins don't
  // support async event (and even if they did, we need to have clipboard accessed synchronously--before the event
  // is gone).
  if (anyPressed(event, BROWSER_SHORTCUTS)) return false;

  const modifiers = getEventModifierKeys(event);
  void keyEvent(id, eventType, event.code, event.timeStamp, modifiers);
  // see note above
  if (isModShortcut(event)) {
    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  return false;
};

function getEditorState(id: number) {
  const store = useDiagramTextEditorsStore();
  return store.editors.get(id);
}

function getEditorInstance(id: number) {
  const store = useDiagramTextEditorsStore();
  return store.editors.get(id)?.editor;
}

function getEditorElement(id: number) {
  const editorState = getEditorState(id);
  return editorState?.editor?.view.dom;
}

export function createEditor(id: number, editorInfoString?: string) {
  const store = useDiagramTextEditorsStore();
  const editorInfo = editorInfoString ? JSON.parse(editorInfoString) as WebRichTextObjectEditorInfo : {};
  store.create(id, editorInfo);
}

export function disposeEditor(id: number) {
  const store = useDiagramTextEditorsStore();
  store.destroy(id);
}

export function getRect(id: number): [number, number, number, number] {
  const element = getEditorElement(id);
  const rect = element?.getBoundingClientRect() ?? {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };
  return [rect.x, rect.y, rect.width, rect.height];
}

export function setRect(id: number, x: number, y: number, width: number, height: number) {
  const state = getEditorState(id);
  if (state) {
    state.rect = {
      x,
      y,
      width,
      height,
    };
  }
}

export function getVisible(id: number): boolean {
  const state = getEditorState(id);
  return state?.visible ?? false;
}

export function setVisible(id: number, val: boolean) {
  const state = getEditorState(id);
  if (state) state.visible = val;
}

export function getIsReadOnly(id: number): boolean {
  const editor = getEditorInstance(id);
  if (!editor) return true;
  return editor.isEditable;
}

export function getAutoFitWidth(id: number): boolean {
  const state = getEditorState(id);
  return Boolean(state?.fitToWidth);
}

export function setAutoFitWidth(id: number, val: boolean) {
  const state = getEditorState(id);
  if (state) state.fitToWidth = val;
}

export function selectAll(id: number) {
  const editor = getEditorInstance(id);
  if (!editor) return;
  editor.commands.selectAll();
}

export function getSelectionStyle(_: number) {
  // console.log(`getSelectionStyle called on ${id}`);
  const style = {};
  const buffers = write(style);
  return [buffers.headerBufferId, buffers.headerSize, buffers.bodyBufferId, buffers.bodySize];
}

export function getSelectionRange(id: number) {
  const editor = getEditorInstance(id);
  if (!editor) return [0, 0];
  return [editor.state.selection.from, editor.state.selection.to];
}

export function getSelectionIsEmpty(id: number): boolean {
  const editor = getEditorInstance(id);
  if (!editor) return true;

  return editor.state.selection.empty;
}

export function getStripsNewlines(_id: number): boolean {
  return true;
}
export function setStripsNewlines(_id: number, _val: boolean) { }

export function setFocus(id: number) {
  const editor = getEditorInstance(id);
  if (!editor) return;
  editor.commands.focus();
}

export function getText(id: number) {
  const state = getEditorState(id);
  return state?.editor ? jsonContentToText(state.editor.getJSON()) : "";
}

export function setText(_id: number, _val: string) {
  console.error("Setting text editor 'Text' is not implemented");
}

export function getAllowUndoRedo(_id: number): boolean {
  return true;
}
export function setAllowUndoRedo(_id: number, _val: boolean) { }
export function canUndo(_id: number): boolean {
  return true;
}
export function canRedo(_id: number): boolean {
  return true;
}
export function getAllowCutCopy(_id: number): boolean {
  return true;
}
export function getAllowPaste(_id: number): boolean {
  return true;
}
export function getAllowDelete(_id: number): boolean {
  return true;
}
export function getAllowSelectAll(_id: number): boolean {
  return true;
}
export function getIsEmpty(_id: number): boolean {
  return true;
}
export function getSelectionCanBecomeLink(_id: number): boolean {
  return true;
}

export function getAcceptsReturn(_id: number): boolean {
  return true;
}
export function setAcceptsReturn(_id: number, _val: boolean) { }

/// This gets called before the text editor goes into focus, but clears the undo stack for this given editor
/// NOTE: The editor always starts in a new instance so this call is a no-op. Plus, implementing a clear history stack might be a pain
export function clearUndoStack(_id: number) {
  // console.log(`clearUndoStack called id: ${id}`);
  // const editorView = this.getEditorView(id);
  // if (!editorView) return;

  // editorView.updateState(editorView.state)
  // console.log(`clearUndoStack - element exists! id: ${id} contents: ${PM.genText(editorView.state.doc)}`);
}

// TODO: isReadOnly logic
export function copy(id: number) {
  console.log(`copy called id: ${id}`);
}
export function cut(id: number) {
  console.log(`cut called id: ${id}`);
}
export function deleteText(id: number) {
  console.log(`delete called id: ${id}`);
}
export function paste(id: number) {
  console.log(`paste called id: ${id}`);
}
export function undo(id: number) {
  console.log(`undo called: id: ${id}`);
}
export function redo(id: number) {
  console.log(`redo called id: ${id}`);
}

/// Set the text style on the text editor element
// export function setCssOnIdFromTextStyle(id: number, textStyle: AxTextStyle) {
//   const div = getEditorElement(id);
//   if (div !== null) {
//     setCssFromTextStyle(div, textStyle);
//   }
// }

export function setAttributedString(id: number, valBuffer: number, len: number, setContent: boolean) {
  const state = getEditorState(id);
  if (!state) return;

  const data = new DataView(Module.HEAPU8.buffer, Module.HEAPU8.byteOffset + valBuffer, len);
  const parsed = read(data).payload as AxAttributedString;
  // console.log(`setAttributedString=${attrStringGetText(parsed)}`);

  const baseStyle = getAxStyleBaseStyle(parsed);
  if (setContent) {
    const jsonContent = attributedStringToJsonContent(parsed);
    state.setContent(jsonContent);
  }
  state.baseStyle = baseStyle;
}

export function setBaseStyle(id: number, valBuffer: number, len: number) {
  const state = getEditorState(id);
  if (!state) return;

  const data = new DataView(Module.HEAPU8.buffer, Module.HEAPU8.byteOffset + valBuffer, len);
  const parsed = read(data).payload as AxAttributedString;
  // console.log(`setAttributedString=${attrStringGetText(parsed)}`);

  const baseStyle = getAxStyleBaseStyle(parsed);
  state.baseStyle = baseStyle;
}

/// Get (and cache) resulting AxAttributedString from the text editor
/// ProseMirror json obj -> AxAttributedString
///
/// OPTIMIZATION: If there were differences, then update the lastAttrStr, write, and return. Else return the original lastAttrStr
/// </summary>
export function getAttributedString(id: number) {
  const attrStr = getAttributedStringObject(id, false);
  return getAttributedStringBufferInfo(attrStr);
}

export function getAttributedStringObject(id: number, fromSelection: boolean) {
  const state = getEditorState(id);
  if (!state?.editor) {
    return getEmptyAttributedString();
  }

  const jsonContent = fromSelection
    ? { type: Document.name, content: state.editor.state.selection.content().toJSON().content }
    : state.editor.getJSON();

  return jsonContentToAttributedString(jsonContent, state.baseStyle ?? { ...defaultParagraphStyle, ...defaultRunStyle });
}

export function getAttributedStringBufferInfo(attrStr: Saveable) {
  const buffers = write(attrStr);
  return [buffers.headerBufferId, buffers.headerSize, buffers.bodyBufferId, buffers.bodySize];
}

export function setJsonValue(id: number, jsonContent: string) {
  const state = getEditorState(id);
  if (!state) return;
  state.setContent(normalize(jsonContent));
}

export function getJsonValue(id: number) {
  const state = getEditorState(id);
  const jsonValue = serialize(state?.editor?.getJSON() ?? {}, "axure-cloud-rich-text");
  if (state) {
    void state.afterSaved.trigger(jsonValue);
  }
  return jsonValue;
}

/// <summary>
/// Apply style to text editor. Requires updating proseMirror model & ensuring AxAttributedString is updated correctly(?)
///
/// TODO: If isRichText -> proceed with PM.applyStyleMarksToSelection, else use applyCssTo
/// </summary>
export function applyStyle(_: number, _valBuffer: number, _len: number) {
  // // Verify that proseMirror is present for the text editor
  // const editor = getEditor(id);
  // if (!editor || editor.isReadOnly) return;

  // // Grab style data from serialized buffer
  // const data = new DataView(Module.HEAPU8.buffer, Module.HEAPU8.byteOffset + valBuffer, len);
  // const parsed = read(data).payload as AxTextStyle;

  // // console.log(`applyStyle - parsed: ${JSON.stringify(parsed)}`);
  // // console.log(`applyStyle - defaultStyle: ${JSON.stringify(defaultStyle)}`);
  // // console.log(`applyStyle - isRichText(): ${this.getIsRichText(id)}`);

  // // Apply style depending on the text editor rich text mode
  // if (getIsRichText(id)) {
  //   // Dispatch the changes to update text editor state and focus on the selection
  //   PM.applyStyleToSelection(editor.view, parsed);
  // } else {
  //   // TODO: Verify apply styles to the textEditor base via parsed AxTextStyle object
  //   // console.log("applyStyle(): isRichText - false!");
  //   setCssFromTextStyle(getEditorElement(id) as HTMLElement, parsed);
  // }
  // console.log(`applyStyle called id: ${id}`);
}

export function setVerticalAlignment(id: number, val: AxStringAlignment) {
  const state = getEditorState(id);
  if (!state) return;
  state.verticalAlign = val;
}

export function getVerticalAlignment(id: number) {
  const state = getEditorState(id);
  if (!state) return AxStringAlignment.Near;
  return state.verticalAlign;
}
